import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';
import { InputField } from './InputField';

interface TypedConfirmDialogProps {
  title: string;
  description: ReactNode;
  /** The exact text the user has to type before the action is enabled */
  confirmText: string;
  confirmLabel: string;
  isLoading?: boolean;
  variant?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

/** Confirmation dialog that requires typing a phrase, used instead of window.confirm. */
export const TypedConfirmDialog = ({
  title,
  description,
  confirmText,
  confirmLabel,
  isLoading,
  variant = 'danger',
  onConfirm,
  onClose,
}: TypedConfirmDialogProps) => {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !isLoading && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLoading, onClose]);

  const matches = typed.trim().toLowerCase() === confirmText.trim().toLowerCase();

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Card className="w-full max-w-md shadow-2xl">
        <div className="flex flex-col items-center text-center py-4">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              variant === 'danger'
                ? 'bg-red-100 dark:bg-red-500/10 text-red-600'
                : 'bg-amber-100 dark:bg-amber-500/10 text-amber-600'
            }`}
          >
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
          <div className="text-sm text-slate-500 mt-2 px-4">{description}</div>

          <form
            className="w-full mt-6 text-left"
            onSubmit={(e) => {
              e.preventDefault();
              if (matches && !isLoading) onConfirm();
            }}
          >
            <InputField
              autoFocus
              label={`Type "${confirmText}" to confirm`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmText}
            />
            <div className="w-full flex gap-3 mt-8">
              <Button type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={variant === 'danger' ? 'danger' : 'primary'}
                className="flex-1"
                disabled={!matches || isLoading}
                isLoading={isLoading}
              >
                {confirmLabel}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};
